import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  LinearProgress,
  Slider,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  ArrowBackIosNew as ArrowBackIcon,
  ArrowForwardIos as ArrowForwardIcon,
  CalendarMonth as CalendarMonthIcon,
  CheckCircle as CheckCircleIcon,
  Close as CloseIcon,
  Edit as EditIcon,
  LocalOffer as LocalOfferIcon,
  PauseCircle as PauseCircleIcon,
  PlayArrow as PlayArrowIcon,
  WarningAmber as WarningAmberIcon,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { addMonths, format, getDay, isBefore, isSameDay, isWithinInterval, startOfDay, startOfMonth } from 'date-fns';
import axios from 'axios';
import Cookies from 'js-cookie';
import CryptoJS from 'crypto-js';
import { AuthContext } from '../App';
import useCustomToast from '../hooks/useCustomToast';
import {
  ALLOWED_API_TYPES,
  CATEGORIES,
  CONFIRM_TEXT,
  PROMO_CATALOG,
  buildApiCatalogMap,
  datesOverlap,
  getCampaignByKey,
  getSuggestedCampaigns,
  humanizeCampaignKey,
  isLegacyPromo,
  isPromoActive,
  isPromoEditable,
  padConfirmCode,
  parseYmd,
  resolveCampaign,
  suggestedRangeFor,
  titleForPromo,
  toYmd,
} from '../config/promoCatalog';

const SECRET_KEY = 'tabtrips_hotel_secret';
const TOKEN_COOKIE_KEY = 'token';
const API = 'https://api.tabtrips.com/olivrweb/hotelapi/dashboardApi.php';
const BRAND = '#FF7700';

const CATEGORY_STYLE = {
  seasonal: { tint: '#ecfdf5', accent: '#0d9488', label: 'Seasonal' },
  festival: { tint: '#fff7ed', accent: '#c2410c', label: 'Festival' },
  weekend: { tint: '#eff6ff', accent: '#2563eb', label: 'Weekend' },
  mega: { tint: '#fef3c7', accent: '#b45309', label: 'Mega' },
  lastminute: { tint: '#fef2f2', accent: '#dc2626', label: 'Last minute' },
};

function getDecryptedToken() {
  const encrypted = Cookies.get(TOKEN_COOKIE_KEY);
  if (!encrypted) return null;
  try {
    const bytes = CryptoJS.AES.decrypt(encrypted, SECRET_KEY);
    return bytes.toString(CryptoJS.enc.Utf8);
  } catch {
    return null;
  }
}

function authPayload() {
  const token = getDecryptedToken();
  const hotelId = localStorage.getItem('selectedHotelId');
  return { token, hotel_id: hotelId ? parseInt(hotelId, 10) : null };
}

function syncToastFrom(data) {
  if (!data || typeof data !== 'object') return null;
  if (data.stayflexi_push || data.stayflexi_cron) {
    return 'Syncing rates for these stay dates…';
  }
  const msg = data.message || '';
  if (/sync|stayflexi|push/i.test(msg)) return msg;
  return null;
}

function MonthCalendar({ monthDate, onMonthChange, coveredRanges, selectedRange, onDayClick, minDate }) {
  const minDay = startOfDay(minDate || new Date());
  const start = startOfMonth(monthDate);
  const lead = getDay(start);
  const daysInMonth = new Date(monthDate.getFullYear(), monthDate.getMonth() + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < lead; i += 1) cells.push(null);
  for (let d = 1; d <= daysInMonth; d += 1) {
    cells.push(new Date(monthDate.getFullYear(), monthDate.getMonth(), d));
  }
  while (cells.length % 7 !== 0) cells.push(null);

  const inCovered = (day) =>
    coveredRanges.some((r) => r.start && r.end && isWithinInterval(day, { start: r.start, end: r.end }));
  const inSelected = (day) => {
    if (!selectedRange?.start) return false;
    const dayNorm = startOfDay(day);
    const startNorm = startOfDay(selectedRange.start);
    if (!selectedRange.end) return isSameDay(dayNorm, startNorm);
    const endNorm = startOfDay(selectedRange.end);
    return isWithinInterval(dayNorm, { start: startNorm, end: endNorm });
  };
  const isRangeStart = (day) =>
    selectedRange?.start && isSameDay(startOfDay(day), startOfDay(selectedRange.start));
  const isRangeEnd = (day) =>
    selectedRange?.end && isSameDay(startOfDay(day), startOfDay(selectedRange.end));

  const handleDayClick = (day) => {
    if (!onDayClick || isBefore(startOfDay(day), minDay)) return;
    onDayClick(startOfDay(day));
  };

  const pickingEnd = selectedRange?.start && !selectedRange?.end;

  return (
    <Box
      sx={{
        background: '#fff',
        borderRadius: '16px',
        border: '1px solid #e8ecf1',
        p: { xs: 1.5, md: 2 },
        boxShadow: '0 4px 20px rgba(15,23,42,0.05)',
      }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
        <IconButton size="small" onClick={() => onMonthChange(addMonths(monthDate, -1))} aria-label="Previous month">
          <ArrowBackIcon fontSize="small" />
        </IconButton>
        <Typography sx={{ fontWeight: 800, letterSpacing: '-0.02em' }}>{format(monthDate, 'MMMM yyyy')}</Typography>
        <IconButton size="small" onClick={() => onMonthChange(addMonths(monthDate, 1))} aria-label="Next month">
          <ArrowForwardIcon fontSize="small" />
        </IconButton>
      </Stack>
      {pickingEnd && (
        <Typography variant="caption" sx={{ display: 'block', mb: 1, color: '#FF7700', fontWeight: 700 }}>
          Now pick your end date
        </Typography>
      )}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.5, mb: 0.5 }}>
        {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((d, i) => (
          <Typography key={`${d}-${i}`} variant="caption" sx={{ textAlign: 'center', fontWeight: 700, color: '#64748b' }}>
            {d}
          </Typography>
        ))}
      </Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.5 }}>
        {cells.map((day, i) => {
          if (!day) return <Box key={`e-${i}`} sx={{ aspectRatio: '1', minHeight: 32 }} />;
          const dayNorm = startOfDay(day);
          const isPast = isBefore(dayNorm, minDay);
          const covered = !isPast && inCovered(dayNorm);
          const selected = inSelected(dayNorm);
          const today = !isPast && isSameDay(dayNorm, new Date());
          const rangeEdge = selected && (isRangeStart(dayNorm) || isRangeEnd(dayNorm));
          return (
            <Box
              key={toYmd(dayNorm)}
              component="button"
              type="button"
              disabled={isPast}
              onClick={() => handleDayClick(dayNorm)}
              aria-label={format(dayNorm, 'MMMM d, yyyy')}
              aria-disabled={isPast}
              sx={{
                appearance: 'none',
                border: 'none',
                aspectRatio: '1',
                minHeight: 32,
                borderRadius: '10px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '0.8rem',
                fontWeight: selected || today ? 800 : 600,
                color: isPast ? '#94a3b8' : selected ? '#fff' : covered ? '#c2410c' : '#334155',
                background: isPast
                  ? '#f1f5f9'
                  : selected
                    ? 'linear-gradient(135deg,#FF7700,#ff9a3c)'
                    : covered
                      ? 'rgba(255,119,0,0.12)'
                      : today
                        ? 'rgba(255,119,0,0.1)'
                        : 'transparent',
                outline: today && !selected ? '2px solid #FF7700' : rangeEdge ? '2px solid rgba(255,255,255,0.8)' : 'none',
                outlineOffset: '-2px',
                cursor: isPast ? 'not-allowed' : 'pointer',
                opacity: isPast ? 0.55 : 1,
                transition: 'transform 0.12s ease, box-shadow 0.12s ease',
                '&:disabled': {
                  cursor: 'not-allowed',
                  opacity: 0.55,
                  color: '#94a3b8',
                  background: '#f1f5f9',
                },
                '&:hover': isPast
                  ? { background: '#f1f5f9', transform: 'none', boxShadow: 'none' }
                  : {
                      transform: 'scale(1.05)',
                      boxShadow: selected ? '0 4px 12px rgba(255,119,0,0.3)' : '0 2px 8px rgba(15,23,42,0.08)',
                    },
              }}
            >
              {dayNorm.getDate()}
            </Box>
          );
        })}
      </Box>
      <Stack direction="row" spacing={2} sx={{ mt: 1.5 }} flexWrap="wrap" useFlexGap>
        <Stack direction="row" spacing={0.75} alignItems="center">
          <Box sx={{ width: 10, height: 10, borderRadius: '3px', bgcolor: 'rgba(255,119,0,0.25)' }} />
          <Typography variant="caption" color="text.secondary">Active promo nights</Typography>
        </Stack>
        <Stack direction="row" spacing={0.75} alignItems="center">
          <Box sx={{ width: 10, height: 10, borderRadius: '3px', background: 'linear-gradient(135deg,#FF7700,#ff9a3c)' }} />
          <Typography variant="caption" color="text.secondary">Selected stay dates</Typography>
        </Stack>
        <Stack direction="row" spacing={0.75} alignItems="center">
          <Box sx={{ width: 10, height: 10, borderRadius: '3px', bgcolor: '#f1f5f9', border: '1px solid #e2e8f0' }} />
          <Typography variant="caption" color="text.secondary">Past dates</Typography>
        </Stack>
      </Stack>
    </Box>
  );
}

function formatSuggestedDates(key) {
  const range = suggestedRangeFor(key);
  if (!range?.start || !range?.end) return '';
  return `${format(range.start, 'MMM d')} – ${format(range.end, 'MMM d')}`;
}

function CampaignCard({ campaign, selected, onSelect, compact, showDates }) {
  const style = CATEGORY_STYLE[campaign.category] || CATEGORY_STYLE.mega;
  const dates = showDates ? formatSuggestedDates(campaign.key) : '';
  return (
    <Box
      component="button"
      type="button"
      onClick={() => onSelect(campaign)}
      sx={{
        appearance: 'none',
        cursor: 'pointer',
        textAlign: 'left',
        width: '100%',
        minHeight: compact ? 168 : 210,
        borderRadius: '22px',
        p: 0,
        bgcolor: '#fff',
        border: '1px solid',
        borderColor: selected ? 'rgba(255,119,0,0.55)' : 'rgba(15,23,42,0.06)',
        boxShadow: selected
          ? '0 18px 40px rgba(255,119,0,0.2)'
          : '0 8px 28px rgba(15,23,42,0.06)',
        transform: selected ? 'translateY(-3px)' : 'none',
        transition: 'transform 0.22s cubic-bezier(.2,.8,.2,1), box-shadow 0.22s ease, border-color 0.22s ease',
        position: 'relative',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        '&:hover': {
          transform: 'translateY(-5px)',
          borderColor: 'rgba(255,119,0,0.35)',
          boxShadow: '0 22px 48px rgba(15,23,42,0.12)',
          '& .promo-card-cta': {
            bgcolor: BRAND,
            color: '#fff',
          },
          '& .promo-card-wash': {
            opacity: 0.22,
            transform: 'scale(1.06)',
          },
          '& .promo-card-badge': {
            transform: 'scale(1.04)',
          },
        },
      }}
    >
      {/* Soft gradient wash */}
      <Box
        className="promo-card-wash"
        sx={{
          position: 'absolute',
          inset: 0,
          background: campaign.gradient,
          opacity: 0.12,
          transition: 'opacity 0.25s ease, transform 0.35s ease',
          pointerEvents: 'none',
        }}
      />
      {/* Decorative orb */}
      <Box
        sx={{
          position: 'absolute',
          top: -36,
          right: -28,
          width: 120,
          height: 120,
          borderRadius: '50%',
          background: campaign.gradient,
          opacity: 0.35,
          filter: 'blur(2px)',
          pointerEvents: 'none',
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          bottom: -40,
          left: -30,
          width: 100,
          height: 100,
          borderRadius: '50%',
          background: style.tint,
          opacity: 0.9,
          pointerEvents: 'none',
        }}
      />

      <Box
        sx={{
          position: 'relative',
          zIndex: 1,
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          p: compact ? 1.75 : 2.25,
          pb: compact ? 1.5 : 1.75,
        }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: compact ? 1.25 : 1.75 }}>
          <Chip
            size="small"
            label={style.label}
            sx={{
              bgcolor: 'rgba(255,255,255,0.85)',
              color: style.accent,
              fontWeight: 800,
              height: 24,
              fontSize: '0.68rem',
              letterSpacing: '0.03em',
              border: `1px solid ${style.accent}22`,
              backdropFilter: 'blur(6px)',
            }}
          />
          <Box
            className="promo-card-badge"
            sx={{
              minWidth: compact ? 58 : 68,
              height: compact ? 58 : 68,
              borderRadius: '18px',
              background: campaign.gradient,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#fff',
              boxShadow: '0 10px 24px rgba(15,23,42,0.18)',
              transition: 'transform 0.22s ease',
            }}
          >
            <Typography sx={{ fontWeight: 900, fontSize: compact ? '1.35rem' : '1.55rem', lineHeight: 1, letterSpacing: '-0.04em' }}>
              {campaign.percent}
            </Typography>
            <Typography sx={{ fontWeight: 800, fontSize: '0.65rem', opacity: 0.92, letterSpacing: '0.04em' }}>
              % OFF
            </Typography>
          </Box>
        </Stack>

        <Typography
          sx={{
            fontWeight: 900,
            fontSize: compact ? '1.05rem' : '1.18rem',
            lineHeight: 1.2,
            color: '#0f172a',
            mb: 0.6,
            letterSpacing: '-0.025em',
            pr: 0.5,
          }}
        >
          {campaign.name}
        </Typography>
        <Typography
          sx={{
            fontSize: compact ? '0.78rem' : '0.84rem',
            color: '#64748b',
            lineHeight: 1.45,
            flex: 1,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {campaign.blurb}
        </Typography>

        {dates ? (
          <Stack direction="row" alignItems="center" spacing={0.6} sx={{ mt: 1.5 }}>
            <CalendarMonthIcon sx={{ fontSize: 14, color: style.accent }} />
            <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#475569' }}>
              {dates}
            </Typography>
          </Stack>
        ) : null}
      </Box>

      <Box
        className="promo-card-cta"
        sx={{
          position: 'relative',
          zIndex: 1,
          mx: compact ? 1.25 : 1.5,
          mb: compact ? 1.25 : 1.5,
          mt: 'auto',
          px: 1.5,
          py: 1,
          borderRadius: '12px',
          bgcolor: 'rgba(15,23,42,0.04)',
          color: '#334155',
          fontWeight: 800,
          fontSize: '0.78rem',
          letterSpacing: '0.01em',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          transition: 'background 0.2s ease, color 0.2s ease',
        }}
      >
        <span>Set up campaign</span>
        <span aria-hidden>→</span>
      </Box>
    </Box>
  );
}

function LastMinuteFeatured({ campaigns, onSelect }) {
  const c = campaigns[0];
  if (!c) return null;
  const dates = formatSuggestedDates(c.key);
  return (
    <Box sx={{ mb: 4 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
        <Box
          sx={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            bgcolor: '#ef4444',
            boxShadow: '0 0 0 4px rgba(239,68,68,0.2)',
            animation: 'pulse 2s infinite',
            '@keyframes pulse': {
              '0%, 100%': { opacity: 1 },
              '50%': { opacity: 0.55 },
            },
          }}
        />
        <Typography sx={{ fontWeight: 900, fontSize: '1.1rem', color: '#0f172a', letterSpacing: '-0.02em' }}>
          Fill empty nights fast
        </Typography>
      </Stack>
      <Box
        component="button"
        type="button"
        onClick={() => onSelect(c)}
        sx={{
          appearance: 'none',
          width: '100%',
          border: '1px solid rgba(255,119,0,0.22)',
          borderRadius: '22px',
          cursor: 'pointer',
          textAlign: 'left',
          bgcolor: '#fff',
          boxShadow: '0 16px 48px rgba(255,119,0,0.12)',
          p: 0,
          overflow: 'hidden',
          transition: 'transform 0.2s ease, box-shadow 0.2s ease',
          '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 20px 52px rgba(255,119,0,0.16)' },
        }}
      >
        <Stack direction={{ xs: 'column', md: 'row' }}>
          <Box
            sx={{
              flex: 1,
              p: { xs: 2.5, md: 3 },
              background: 'linear-gradient(135deg, #fff7ed 0%, #ffffff 100%)',
              borderRight: { md: '1px solid rgba(255,119,0,0.12)' },
            }}
          >
            <Chip
              label="Recommended · Last minute"
              size="small"
              sx={{
                mb: 1.5,
                fontWeight: 800,
                bgcolor: '#ffedd5',
                color: '#c2410c',
                height: 24,
              }}
            />
            <Typography sx={{ fontWeight: 900, fontSize: { xs: '1.4rem', md: '1.65rem' }, lineHeight: 1.15, color: '#0f172a', mb: 1 }}>
              {c.name}
            </Typography>
            <Typography sx={{ color: '#64748b', fontSize: '0.95rem', lineHeight: 1.5, maxWidth: 480, mb: dates ? 1.5 : 2 }}>
              {c.blurb}
            </Typography>
            {dates ? (
              <Stack direction="row" alignItems="center" spacing={0.75}>
                <CalendarMonthIcon sx={{ fontSize: 16, color: BRAND }} />
                <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: '#475569' }}>
                  {dates}
                </Typography>
              </Stack>
            ) : null}
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              p: { xs: 2.5, md: 3 },
              minWidth: { md: 200 },
              background: 'linear-gradient(160deg, #FF7700 0%, #fb923c 100%)',
              color: '#fff',
            }}
          >
            <Typography sx={{ fontWeight: 900, fontSize: '3.25rem', lineHeight: 1, letterSpacing: '-0.04em' }}>
              {c.percent}%
            </Typography>
            <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', opacity: 0.92, mb: 2 }}>
              off stay dates
            </Typography>
            <Box
              sx={{
                px: 2.5,
                py: 1,
                borderRadius: 99,
                bgcolor: 'rgba(255,255,255,0.22)',
                fontWeight: 800,
                fontSize: '0.85rem',
              }}
            >
              Set up now →
            </Box>
          </Box>
        </Stack>
      </Box>
    </Box>
  );
}

function SuggestedSection({ campaigns, onSelect }) {
  if (!campaigns.length) return null;
  return (
    <Box
      sx={{
        mb: 4,
        borderRadius: '22px',
        border: '1px solid #e8ecf1',
        bgcolor: '#fff',
        boxShadow: '0 8px 28px rgba(15,23,42,0.05)',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          px: { xs: 2, md: 2.5 },
          py: 2,
          background: 'linear-gradient(90deg, #f8fafc 0%, #fff7ed 100%)',
          borderBottom: '1px solid #f1f5f9',
        }}
      >
        <Typography sx={{ fontWeight: 900, fontSize: '1.05rem', color: '#0f172a', letterSpacing: '-0.02em' }}>
          Suggested for right now
        </Typography>
        <Typography sx={{ fontSize: '0.85rem', color: '#64748b', mt: 0.35 }}>
          Seasonal picks with dates pre-filled — tap one to review and publish.
        </Typography>
      </Box>
      <Box
        sx={{
          display: 'grid',
          gridAutoFlow: 'column',
          gridAutoColumns: { xs: '84%', sm: '280px' },
          gap: 1.75,
          overflowX: 'auto',
          p: 2,
          scrollSnapType: 'x mandatory',
          '& > *': { scrollSnapAlign: 'start' },
          '&::-webkit-scrollbar': { height: 6 },
          '&::-webkit-scrollbar-thumb': { bgcolor: '#cbd5e1', borderRadius: 99 },
        }}
      >
        {campaigns.map((c) => (
          <CampaignCard key={c.key} campaign={c} compact showDates selected={false} onSelect={onSelect} />
        ))}
      </Box>
    </Box>
  );
}

function DeactivateModal({ open, promo, onClose, onConfirm, loading }) {
  const [step, setStep] = useState(0);
  const [ack, setAck] = useState(false);
  const [slider, setSlider] = useState(0);

  const expectedCode = padConfirmCode(promo?.id);
  const totalSteps = 3;

  useEffect(() => {
    if (!open) {
      setStep(0);
      setAck(false);
      setSlider(0);
    }
  }, [open]);

  const canDeactivate = ack && slider >= 100;

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '20px',
          bgcolor: '#fff',
          color: '#0f172a',
          border: '1px solid #fecaca',
          boxShadow: '0 24px 64px rgba(15,23,42,0.14)',
        },
      }}
    >
      <DialogTitle sx={{ fontWeight: 900, pr: 6, color: '#991b1b' }}>
        Deactivate this promotion?
        <IconButton onClick={onClose} disabled={loading} sx={{ position: 'absolute', right: 12, top: 12, color: '#64748b' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <LinearProgress
          variant="determinate"
          value={((step + 1) / totalSteps) * 100}
          sx={{ mb: 2.5, height: 6, borderRadius: 99, bgcolor: '#fee2e2', '& .MuiLinearProgress-bar': { bgcolor: '#dc2626' } }}
        />
        {step === 0 && (
          <Alert severity="warning" icon={<WarningAmberIcon />} sx={{ mb: 2 }}>
            Turning this off means guests will see regular prices for these stay dates. You may get fewer bookings.
          </Alert>
        )}
        {step === 1 && (
          <FormControlLabel
            control={<Checkbox checked={ack} onChange={(e) => setAck(e.target.checked)} sx={{ color: '#FF7700', '&.Mui-checked': { color: '#FF7700' } }} />}
            label="I understand this may reduce bookings."
          />
        )}
        {step === 2 && (
          <Box>
            <Typography sx={{ mb: 1.5, fontWeight: 700 }}>Slide to confirm deactivation</Typography>
            <Slider
              value={slider}
              onChange={(_, v) => setSlider(v)}
              min={0}
              max={100}
              sx={{ color: '#FF7700' }}
            />
            <Typography variant="caption" sx={{ opacity: 0.7 }}>
              {slider < 100 ? 'Slide all the way to confirm.' : 'Confirmed.'}
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} disabled={loading} sx={{ color: '#64748b', textTransform: 'none', fontWeight: 700 }}>
          Keep live
        </Button>
        {step < totalSteps - 1 ? (
          <Button
            variant="contained"
            onClick={() => setStep((s) => s + 1)}
            disabled={step === 1 && !ack}
            sx={{ textTransform: 'none', bgcolor: '#475569', '&:hover': { bgcolor: '#334155' }, fontWeight: 700 }}
          >
            Continue
          </Button>
        ) : (
          <Button
            color="error"
            variant="contained"
            disabled={!canDeactivate || loading}
            onClick={() => onConfirm({ confirm_text: CONFIRM_TEXT, confirm_code: expectedCode })}
            sx={{ textTransform: 'none', minWidth: 140 }}
          >
            {loading ? <CircularProgress size={18} color="inherit" /> : 'Deactivate'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

function ConfigureModal({
  open,
  onClose,
  campaign,
  editingId,
  percent,
  setPercent,
  startDate,
  setStartDate,
  endDate,
  setEndDate,
  monthDate,
  setMonthDate,
  formErrors,
  overlapWarning,
  saving,
  onSave,
  onReset,
  coveredRanges,
  onClearDateErrors,
}) {
  const title = campaign?.name || (editingId ? 'Edit promotion' : 'Set up promotion');

  const handleCalendarDayClick = (day) => {
    if (!startDate || (startDate && endDate)) {
      setStartDate(day);
      setEndDate(null);
    } else {
      const start = startOfDay(startDate);
      if (isBefore(day, start)) {
        setEndDate(start);
        setStartDate(day);
      } else {
        setEndDate(day);
      }
    }
    onClearDateErrors?.();
  };

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      maxWidth="md"
      fullWidth
      scroll="paper"
      PaperProps={{
        sx: {
          borderRadius: '20px',
          overflow: 'hidden',
          boxShadow: '0 24px 64px rgba(15,23,42,0.18)',
        },
      }}
    >
      <Box sx={{ height: 6, background: campaign?.gradient || 'linear-gradient(90deg,#FF7700,#fdba74)' }} />
      <DialogTitle sx={{ fontWeight: 900, letterSpacing: '-0.02em', pb: 0.5, pr: 6 }}>
        {title}
        <IconButton
          onClick={onClose}
          disabled={saving}
          sx={{ position: 'absolute', right: 12, top: 14, color: '#64748b' }}
          aria-label="Close"
        >
          <CloseIcon />
        </IconButton>
        {campaign?.blurb && (
          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, mt: 0.5 }}>
            {campaign.blurb}
          </Typography>
        )}
      </DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        <Grid container spacing={2.5}>
          <Grid item xs={12} md={5}>
            <MonthCalendar
              monthDate={monthDate}
              onMonthChange={setMonthDate}
              coveredRanges={coveredRanges}
              selectedRange={{ start: startDate, end: endDate }}
              onDayClick={handleCalendarDayClick}
              minDate={new Date()}
            />
          </Grid>
          <Grid item xs={12} md={7}>
            <Box
              sx={{
                bgcolor: '#f8fafc',
                borderRadius: '16px',
                border: '1px solid #e2e8f0',
                p: { xs: 2, sm: 2.5 },
                height: '100%',
              }}
            >
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Discount %"
                    value={percent}
                    onChange={(e) => setPercent(e.target.value.replace(/[^\d.]/g, ''))}
                    error={!!formErrors.percent}
                    helperText={formErrors.percent}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">%</InputAdornment>,
                      sx: { bgcolor: '#fff', borderRadius: 2 },
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <DatePicker
                    label="Start date"
                    value={startDate}
                    onChange={(v) => {
                      setStartDate(v);
                      if (v) setMonthDate(startOfMonth(v));
                    }}
                    minDate={new Date()}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        fullWidth
                        error={!!formErrors.startDate}
                        helperText={formErrors.startDate}
                        InputProps={{ ...params.InputProps, sx: { bgcolor: '#fff', borderRadius: 2 } }}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <DatePicker
                    label="End date"
                    value={endDate}
                    onChange={setEndDate}
                    minDate={startDate || new Date()}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        fullWidth
                        error={!!formErrors.endDate}
                        helperText={formErrors.endDate}
                        InputProps={{ ...params.InputProps, sx: { bgcolor: '#fff', borderRadius: 2 } }}
                      />
                    )}
                  />
                </Grid>
              </Grid>
              {overlapWarning && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  These dates overlap another active promotion.
                </Alert>
              )}
              {formErrors.campaign && (
                <Alert severity="error" sx={{ mt: 2 }}>{formErrors.campaign}</Alert>
              )}
            </Box>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, pt: 1, gap: 1 }}>
        <Button onClick={onReset} disabled={saving} sx={{ textTransform: 'none', color: '#64748b' }}>
          Reset
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button onClick={onClose} disabled={saving} variant="outlined" sx={{ textTransform: 'none', borderRadius: 2 }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={saving}
          onClick={onSave}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <AddIcon />}
          sx={{
            textTransform: 'none',
            fontWeight: 800,
            px: 3,
            borderRadius: 2,
            background: 'linear-gradient(135deg,#FF7700,#ff9a3c)',
            boxShadow: '0 8px 20px rgba(255,119,0,0.28)',
            '&:hover': { background: 'linear-gradient(135deg,#e56b00,#FF7700)' },
          }}
        >
          {editingId ? 'Save changes' : 'Create promotion'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

const Promotions = () => {
  const { handleInvalidToken } = useContext(AuthContext) || {};
  const { successToast, errorToast, infoToast, warningToast } = useCustomToast();

  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [statusBusy, setStatusBusy] = useState(false);
  const [error, setError] = useState('');
  const [discounts, setDiscounts] = useState([]);
  const [legacyDiscounts, setLegacyDiscounts] = useState([]);
  const [apiCatalogMap, setApiCatalogMap] = useState({});
  const [listFilter, setListFilter] = useState('all');
  const promoInFlightRef = useRef(null);
  const lastPromoHotelRef = useRef(null);
  const lastPromoFetchedAtRef = useRef(0);

  const [campaignKey, setCampaignKey] = useState('');
  const [apiType, setApiType] = useState('tabtrips_promotion');
  const [percent, setPercent] = useState('');
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [formErrors, setFormErrors] = useState({});
  const [monthDate, setMonthDate] = useState(() => startOfMonth(new Date()));

  const [activatePromo, setActivatePromo] = useState(null);
  const [deactivatePromo, setDeactivatePromo] = useState(null);
  const [configureOpen, setConfigureOpen] = useState(false);

  const selectedCampaign = resolveCampaign(campaignKey, apiCatalogMap);
  const suggestions = useMemo(
    () => getSuggestedCampaigns(new Date()).map((c) => resolveCampaign(c.key, apiCatalogMap) || c),
    [apiCatalogMap]
  );
  const catalogEntries = useMemo(
    () =>
      PROMO_CATALOG.map((c) => {
        const api = apiCatalogMap[c.key];
        if (!api) return c;
        return {
          ...c,
          name: api.campaign_name || api.display_name || c.name,
          type: ALLOWED_API_TYPES.includes(api.type) ? api.type : c.type,
        };
      }),
    [apiCatalogMap]
  );

  const lastMinuteCampaigns = useMemo(
    () => catalogEntries.filter((c) => c.category === 'lastminute'),
    [catalogEntries]
  );

  const suggestionsForSeason = useMemo(
    () => suggestions.filter((c) => c.category !== 'lastminute'),
    [suggestions]
  );

  const allRows = useMemo(() => {
    const tagged = [
      ...discounts.map((d) => ({ ...d, _legacy: isLegacyPromo(d) })),
      ...legacyDiscounts.map((d) => ({ ...d, _legacy: true })),
    ];
    const seen = new Set();
    return tagged.filter((d) => {
      const k = String(d.id);
      if (seen.has(k)) return false;
      seen.add(k);
      return true;
    });
  }, [discounts, legacyDiscounts]);

  const coveredRanges = useMemo(
    () =>
      allRows
        .filter(isPromoActive)
        .map((p) => ({ start: parseYmd(p.start_date), end: parseYmd(p.end_date) }))
        .filter((r) => r.start && r.end),
    [allRows]
  );

  const overlapWarning = useMemo(() => {
    if (!startDate || !endDate) return false;
    return allRows.some((p) => {
      if (!isPromoActive(p)) return false;
      if (editingId && String(p.id) === String(editingId)) return false;
      return datesOverlap(startDate, endDate, parseYmd(p.start_date), parseYmd(p.end_date));
    });
  }, [allRows, startDate, endDate, editingId]);

  const fetchPromos = useCallback(async (opts = {}) => {
    const force = opts.force === true;
    const { token, hotel_id } = authPayload();
    if (!token || !hotel_id) {
      setError('Select a hotel and sign in again.');
      return;
    }
    const hotelKey = String(hotel_id);
    if (promoInFlightRef.current === hotelKey) return;
    if (
      !force &&
      lastPromoHotelRef.current === hotelKey &&
      Date.now() - lastPromoFetchedAtRef.current < 2500
    ) {
      return;
    }

    promoInFlightRef.current = hotelKey;
    setLoading(true);
    setError('');
    try {
      const response = await axios.post(
        `${API}?action=getDiscuontDetails`,
        { token, hotel_id },
        { headers: { 'Content-Type': 'application/json' } }
      );
      const data = response.data || {};
      if (data.status) {
        setDiscounts(Array.isArray(data.discount) ? data.discount : []);
        setLegacyDiscounts(Array.isArray(data.legacy_discount) ? data.legacy_discount : []);
        setApiCatalogMap(buildApiCatalogMap(data.campaign_catalog));
        lastPromoHotelRef.current = hotelKey;
        lastPromoFetchedAtRef.current = Date.now();
      } else {
        const msg = data.message || 'Failed to fetch promotions.';
        const lower = String(msg).toLowerCase();
        if (lower.includes('no discount') || lower.includes('no promotion')) {
          setDiscounts([]);
          setLegacyDiscounts([]);
          lastPromoHotelRef.current = hotelKey;
          lastPromoFetchedAtRef.current = Date.now();
        } else {
          if (lower.includes('token') || lower.includes('unauthorized')) handleInvalidToken?.();
          setError(msg);
        }
      }
    } catch (err) {
      if (err.response?.status === 401 || err.response?.status === 403) handleInvalidToken?.();
      setError('Failed to fetch promotions. Please try again.');
    } finally {
      if (promoInFlightRef.current === hotelKey) promoInFlightRef.current = null;
      setLoading(false);
    }
  }, [handleInvalidToken]);

  useEffect(() => {
    fetchPromos();
    // Only refetch when the selected hotel actually changes — not on every hotelsUpdated/storage ping.
    const onHotelSelected = () => fetchPromos({ force: true });
    window.addEventListener('hotelSelected', onHotelSelected);
    return () => {
      window.removeEventListener('hotelSelected', onHotelSelected);
    };
  }, [fetchPromos]);

  const applyCampaign = (campaign) => {
    setEditingId(null);
    setCampaignKey(campaign.key);
    setApiType(ALLOWED_API_TYPES.includes(campaign.type) ? campaign.type : 'tabtrips_promotion');
    setPercent(String(campaign.percent));
    const range = suggestedRangeFor(campaign.key);
    setStartDate(range.start);
    setEndDate(range.end);
    setMonthDate(startOfMonth(range.start));
    setFormErrors({});
    setConfigureOpen(true);
  };

  const resetForm = () => {
    setCampaignKey('');
    setApiType('tabtrips_promotion');
    setPercent('');
    setStartDate(null);
    setEndDate(null);
    setEditingId(null);
    setFormErrors({});
  };

  const closeConfigure = () => {
    if (saving) return;
    setConfigureOpen(false);
    resetForm();
  };

  const validate = () => {
    const errors = {};
    if (!campaignKey) errors.campaign = 'Choose a campaign';
    const camp = resolveCampaign(campaignKey, apiCatalogMap);
    const editingRow = editingId
      ? discounts.find((d) => String(d.id) === String(editingId))
      : null;
    const resolvedName =
      camp?.name ||
      editingRow?.campaign_name ||
      editingRow?.display_name ||
      (campaignKey ? humanizeCampaignKey(campaignKey) : '');
    if (campaignKey && !String(resolvedName || '').trim()) {
      errors.campaign = 'Campaign name is missing';
    }
    const n = Number(percent);
    if (!percent || Number.isNaN(n) || n < 1 || n > 95) errors.percent = 'Enter 1–95%';
    if (!startDate) errors.startDate = 'Start date is required';
    if (!endDate) errors.endDate = 'End date is required';
    if (startDate && endDate && endDate < startDate) errors.endDate = 'End date must be on or after start';
    if (!ALLOWED_API_TYPES.includes(apiType)) errors.campaign = 'This campaign type cannot be created';
    return errors;
  };

  const handleSave = async () => {
    const errors = validate();
    if (Object.keys(errors).length) {
      setFormErrors(errors);
      return;
    }
    const { token, hotel_id } = authPayload();
    if (!token || !hotel_id) {
      errorToast('Select a hotel and sign in again.');
      return;
    }
    setSaving(true);
    try {
      const camp = resolveCampaign(campaignKey, apiCatalogMap);
      const editingRow = editingId
        ? discounts.find((d) => String(d.id) === String(editingId))
        : null;
      const campaignName =
        camp?.name ||
        editingRow?.campaign_name ||
        editingRow?.display_name ||
        (campaignKey ? humanizeCampaignKey(campaignKey) : '') ||
        'Tabtrips Promotion';

      const payload = {
        token,
        hotel_id,
        campaign_key: campaignKey,
        campaign_name: campaignName,
        type: apiType,
        min_percent: parseInt(percent, 10),
        start_date: toYmd(startDate),
        end_date: toYmd(endDate),
      };
      if (editingId) payload.id = editingId;

      infoToast('Saving promotion…');
      const action = editingId ? 'updateDiscount' : 'setDiscount';
      const response = await axios.post(`${API}?action=${action}`, payload, {
        headers: { 'Content-Type': 'application/json' },
      });
      const data = response.data || {};
      if (data.status) {
        successToast(editingId ? 'Promotion updated.' : 'Promotion created.');
        const syncMsg = syncToastFrom(data);
        if (syncMsg) infoToast(syncMsg);
        setConfigureOpen(false);
        await fetchPromos({ force: true });
        resetForm();
        setTabValue(1);
      } else {
        errorToast(data.message || 'Failed to save promotion.');
      }
    } catch (err) {
      errorToast(err.response?.data?.message || 'Failed to save promotion.');
    } finally {
      setSaving(false);
    }
  };

  const handleActivate = async () => {
    if (!activatePromo?.id) return;
    const { token, hotel_id } = authPayload();
    setStatusBusy(true);
    try {
      const response = await axios.post(
        `${API}?action=setDiscountStatus`,
        { token, hotel_id, id: activatePromo.id, status: 0 },
        { headers: { 'Content-Type': 'application/json' } }
      );
      const data = response.data || {};
      if (data.status) {
        successToast('Promotion activated for these stay dates.');
        const syncMsg = syncToastFrom(data);
        if (syncMsg) infoToast(syncMsg);
        setActivatePromo(null);
        await fetchPromos({ force: true });
      } else {
        errorToast(data.message || 'Failed to activate.');
      }
    } catch (err) {
      errorToast(err.response?.data?.message || 'Failed to activate.');
    } finally {
      setStatusBusy(false);
    }
  };

  const handleDeactivate = async ({ confirm_text, confirm_code }) => {
    if (!deactivatePromo?.id) return;
    const { token, hotel_id } = authPayload();
    setStatusBusy(true);
    try {
      const response = await axios.post(
        `${API}?action=setDiscountStatus`,
        {
          token,
          hotel_id,
          id: deactivatePromo.id,
          status: 1,
          deactivate_confirm: true,
          confirm_text,
          confirm_code,
        },
        { headers: { 'Content-Type': 'application/json' } }
      );
      const data = response.data || {};
      if (data.status) {
        warningToast('Promotion turned off.');
        const syncMsg = syncToastFrom(data);
        if (syncMsg) infoToast(syncMsg);
        setDeactivatePromo(null);
        await fetchPromos({ force: true });
      } else {
        errorToast(data.message || 'Failed to deactivate.');
      }
    } catch (err) {
      errorToast(err.response?.data?.message || 'Failed to deactivate.');
    } finally {
      setStatusBusy(false);
    }
  };

  const startEdit = (promo) => {
    if (!isPromoEditable(promo)) return;
    const camp = resolveCampaign(promo.campaign_key, apiCatalogMap) || {
      key: promo.campaign_key || promo.discount_type,
      name: titleForPromo(promo, apiCatalogMap),
      type: promo.discount_type,
      percent: promo.min_percentage,
      blurb: 'Update discount and stay dates.',
      gradient: 'linear-gradient(135deg,#5b21b6,#FF7700)',
      category: 'mega',
    };
    setEditingId(promo.id);
    setCampaignKey(camp.key);
    setApiType(ALLOWED_API_TYPES.includes(promo.discount_type) ? promo.discount_type : 'tabtrips_promotion');
    setPercent(String(promo.min_percentage ?? camp.percent ?? ''));
    setStartDate(parseYmd(promo.start_date));
    setEndDate(parseYmd(promo.end_date));
    if (promo.start_date) setMonthDate(startOfMonth(parseYmd(promo.start_date)));
    setFormErrors({});
    setConfigureOpen(true);
  };

  const filteredList = allRows.filter((p) => {
    const active = isPromoActive(p);
    const legacy = isLegacyPromo(p);
    if (listFilter === 'active') return active && !legacy;
    if (listFilter === 'inactive') return !active && !legacy;
    if (listFilter === 'legacy') return legacy;
    return true;
  });
  const liveList = filteredList.filter((p) => !isLegacyPromo(p));
  const deprecatedList = (listFilter === 'all' || listFilter === 'legacy')
    ? filteredList.filter((p) => isLegacyPromo(p))
    : [];

  const renderPromoCard = (promo) => {
    const active = isPromoActive(promo);
    const legacy = isLegacyPromo(promo);
    const editable = isPromoEditable(promo);
    const camp = resolveCampaign(promo.campaign_key, apiCatalogMap);
    const style = camp ? CATEGORY_STYLE[camp.category] || CATEGORY_STYLE.mega : CATEGORY_STYLE.mega;
    const gradient = camp?.gradient || (legacy ? 'linear-gradient(135deg,#d97706,#fbbf24)' : 'linear-gradient(135deg,#64748b,#94a3b8)');
    return (
      <Grid item xs={12} sm={6} md={4} key={promo.id}>
        <Card
          elevation={0}
          sx={{
            height: '100%',
            borderRadius: '22px',
            border: '1px solid',
            borderColor: legacy ? '#fde68a' : active ? '#bbf7d0' : 'rgba(15,23,42,0.06)',
            overflow: 'hidden',
            bgcolor: '#fff',
            position: 'relative',
            boxShadow: '0 8px 28px rgba(15,23,42,0.06)',
            transition: 'transform 0.22s cubic-bezier(.2,.8,.2,1), box-shadow 0.22s ease',
            '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 18px 40px rgba(15,23,42,0.1)' },
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              background: gradient,
              opacity: 0.08,
              pointerEvents: 'none',
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              top: -30,
              right: -24,
              width: 100,
              height: 100,
              borderRadius: '50%',
              background: gradient,
              opacity: 0.28,
              pointerEvents: 'none',
            }}
          />
          <CardContent sx={{ p: 2.25, position: 'relative', zIndex: 1 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1} sx={{ mb: 1.75 }}>
              <Box sx={{ flex: 1, minWidth: 0, pr: 1 }}>
                <Chip
                  size="small"
                  label={legacy ? 'Legacy' : active ? 'Live' : 'Paused'}
                  sx={{
                    fontWeight: 800,
                    height: 22,
                    mb: 1,
                    bgcolor: legacy ? '#fef3c7' : active ? '#dcfce7' : '#f1f5f9',
                    color: legacy ? '#b45309' : active ? '#15803d' : '#64748b',
                  }}
                />
                <Typography sx={{ fontWeight: 900, fontSize: '1.08rem', color: '#0f172a', lineHeight: 1.25, letterSpacing: '-0.02em' }}>
                  {titleForPromo(promo, apiCatalogMap)}
                </Typography>
              </Box>
              <Box
                sx={{
                  minWidth: 64,
                  height: 64,
                  borderRadius: '16px',
                  background: gradient,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#fff',
                  boxShadow: '0 10px 22px rgba(15,23,42,0.16)',
                  flexShrink: 0,
                }}
              >
                <Typography sx={{ fontWeight: 900, fontSize: '1.4rem', lineHeight: 1, letterSpacing: '-0.04em' }}>
                  {promo.min_percentage}
                </Typography>
                <Typography sx={{ fontWeight: 800, fontSize: '0.62rem', opacity: 0.92 }}>% OFF</Typography>
              </Box>
            </Stack>
            <Box
              sx={{
                bgcolor: 'rgba(255,255,255,0.75)',
                border: '1px solid rgba(15,23,42,0.06)',
                borderRadius: '12px',
                px: 1.5,
                py: 1,
                mb: 2,
                backdropFilter: 'blur(4px)',
              }}
            >
              <Stack direction="row" alignItems="center" spacing={0.75}>
                <CalendarMonthIcon sx={{ fontSize: 15, color: style.accent }} />
                <Typography variant="body2" sx={{ fontWeight: 700, color: '#475569', fontSize: '0.82rem' }}>
                  {promo.start_date} → {promo.end_date}
                </Typography>
              </Stack>
            </Box>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {editable && (
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<EditIcon />}
                  onClick={() => startEdit(promo)}
                  sx={{ textTransform: 'none', borderRadius: 2, fontWeight: 700, borderColor: '#e2e8f0', color: '#475569', bgcolor: 'rgba(255,255,255,0.7)' }}
                >
                  Edit
                </Button>
              )}
              {!legacy && !active && (
                <Button
                  size="small"
                  color="success"
                  variant="contained"
                  startIcon={<PlayArrowIcon />}
                  onClick={() => setActivatePromo(promo)}
                  sx={{ textTransform: 'none', boxShadow: 'none', borderRadius: 2, fontWeight: 700 }}
                >
                  Activate
                </Button>
              )}
              {active && (
                <Button
                  size="small"
                  color="error"
                  variant="outlined"
                  startIcon={<PauseCircleIcon />}
                  onClick={() => setDeactivatePromo(promo)}
                  sx={{ textTransform: 'none', borderRadius: 2, fontWeight: 700, bgcolor: 'rgba(255,255,255,0.7)' }}
                >
                  Deactivate
                </Button>
              )}
            </Stack>
          </CardContent>
        </Card>
      </Grid>
    );
  };

  const activeCount = useMemo(
    () => allRows.filter((p) => isPromoActive(p) && !isLegacyPromo(p)).length,
    [allRows]
  );

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ p: { xs: 1.5, md: 3 }, pb: 6, bgcolor: '#faf9f7', minHeight: '100%' }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ sm: 'center' }}
          justifyContent="space-between"
          spacing={2}
          sx={{ mb: 3 }}
        >
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: '14px',
                background: 'linear-gradient(135deg, #FF7700, #fdba74)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 8px 20px rgba(255,119,0,0.25)',
              }}
            >
              <LocalOfferIcon sx={{ color: '#fff' }} />
            </Box>
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 900, letterSpacing: '-0.03em', color: '#0f172a', lineHeight: 1.1 }}>
                Promotions
              </Typography>
              <Typography sx={{ color: '#64748b', fontSize: '0.9rem', mt: 0.25 }}>
                Discount stay dates to win more bookings
              </Typography>
            </Box>
          </Stack>
          {activeCount > 0 && (
            <Box
              sx={{
                px: 2,
                py: 1,
                borderRadius: '14px',
                bgcolor: '#fff',
                border: '1px solid #e2e8f0',
                boxShadow: '0 4px 12px rgba(15,23,42,0.04)',
              }}
            >
              <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                Live now
              </Typography>
              <Typography sx={{ fontWeight: 900, fontSize: '1.35rem', color: '#15803d', lineHeight: 1.1 }}>
                {activeCount} active
              </Typography>
            </Box>
          )}
        </Stack>

        <Box
          sx={{
            display: 'inline-flex',
            bgcolor: '#fff',
            border: '1px solid #e8ecf1',
            borderRadius: '14px',
            p: 0.5,
            mb: 3,
            boxShadow: '0 2px 8px rgba(15,23,42,0.04)',
          }}
        >
          {[
            { v: 0, label: '+ Create new', icon: <AddIcon sx={{ fontSize: 18 }} /> },
            { v: 1, label: 'All promotions', icon: <LocalOfferIcon sx={{ fontSize: 18 }} /> },
          ].map((t) => (
            <Button
              key={t.v}
              onClick={() => setTabValue(t.v)}
              startIcon={t.icon}
              sx={{
                textTransform: 'none',
                fontWeight: 800,
                borderRadius: '10px',
                px: 2,
                py: 1,
                color: tabValue === t.v ? '#fff' : '#64748b',
                bgcolor: tabValue === t.v ? BRAND : 'transparent',
                boxShadow: tabValue === t.v ? '0 4px 12px rgba(255,119,0,0.28)' : 'none',
                '&:hover': {
                  bgcolor: tabValue === t.v ? '#e56b00' : '#f8fafc',
                },
              }}
            >
              {t.label}
            </Button>
          ))}
        </Box>

        {tabValue === 0 && (
          <Box>
            <LastMinuteFeatured campaigns={lastMinuteCampaigns} onSelect={applyCampaign} />

            <SuggestedSection campaigns={suggestionsForSeason} onSelect={applyCampaign} />

            {CATEGORIES.filter((cat) => cat.id !== 'lastminute').map((cat) => {
              const items = catalogEntries.filter((c) => c.category === cat.id);
              if (!items.length) return null;
              const catStyle = CATEGORY_STYLE[cat.id] || CATEGORY_STYLE.mega;
              return (
                <Box key={cat.id} sx={{ mb: 4 }}>
                  <Stack direction="row" alignItems="center" spacing={1.25} sx={{ mb: 1.5 }}>
                    <Box sx={{ width: 4, height: 22, borderRadius: 99, bgcolor: catStyle.accent }} />
                    <Typography sx={{ fontWeight: 900, color: '#0f172a', fontSize: '1.05rem', letterSpacing: '-0.02em' }}>
                      {cat.label}
                    </Typography>
                    <Typography sx={{ color: '#94a3b8', fontSize: '0.82rem', fontWeight: 600 }}>
                      {items.length} campaigns
                    </Typography>
                  </Stack>
                  <Grid container spacing={1.75}>
                    {items.map((c) => (
                      <Grid item xs={12} sm={6} md={4} lg={3} key={c.key}>
                        <CampaignCard campaign={c} selected={false} onSelect={applyCampaign} />
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              );
            })}
          </Box>
        )}

        {tabValue === 1 && (
          <Box>
            <Stack direction="row" spacing={1} sx={{ mb: 2.5 }} flexWrap="wrap" useFlexGap>
              {[
                { id: 'all', label: 'All' },
                { id: 'active', label: 'Live' },
                { id: 'inactive', label: 'Paused' },
                { id: 'legacy', label: 'Legacy' },
              ].map((f) => (
                <Chip
                  key={f.id}
                  label={f.label}
                  onClick={() => setListFilter(f.id)}
                  sx={{
                    fontWeight: 800,
                    height: 34,
                    bgcolor: listFilter === f.id ? BRAND : '#fff',
                    color: listFilter === f.id ? '#fff' : '#475569',
                    border: listFilter === f.id ? 'none' : '1px solid #e2e8f0',
                    boxShadow: listFilter === f.id ? '0 4px 12px rgba(255,119,0,0.22)' : 'none',
                  }}
                />
              ))}
            </Stack>
            {loading ? (
              <Box sx={{ textAlign: 'center', py: 8 }}>
                <CircularProgress sx={{ color: '#FF7700' }} />
                <Typography sx={{ mt: 2 }} color="text.secondary">Loading promotions…</Typography>
              </Box>
            ) : error ? (
              <Alert severity="error">{error}</Alert>
            ) : liveList.length === 0 && deprecatedList.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 8, borderRadius: '18px', bgcolor: '#f8fafc', border: '1px dashed #cbd5e1' }}>
                <CalendarMonthIcon sx={{ fontSize: 48, color: '#94a3b8', mb: 1 }} />
                <Typography variant="h6" sx={{ fontWeight: 800, mb: 1 }}>No promotions yet</Typography>
                <Typography color="text.secondary" sx={{ mb: 2 }}>
                  Pick a campaign and set your discount to start getting more bookings.
                </Typography>
                <Button
                  variant="contained"
                  onClick={() => setTabValue(0)}
                  sx={{ textTransform: 'none', bgcolor: '#FF7700', '&:hover': { bgcolor: '#e56b00' } }}
                >
                  + Create new
                </Button>
              </Box>
            ) : (
              <>
                {liveList.length > 0 && (
                  <Grid container spacing={2} sx={{ mb: 3 }}>
                    {liveList.map(renderPromoCard)}
                  </Grid>
                )}
                {deprecatedList.length > 0 && (
                  <Box>
                    <Typography sx={{ fontWeight: 800, mb: 1.5, color: '#b45309' }}>
                      Older promotions
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      These can only be turned off.
                    </Typography>
                    <Grid container spacing={2}>
                      {deprecatedList.map(renderPromoCard)}
                    </Grid>
                  </Box>
                )}
              </>
            )}
          </Box>
        )}

        <Dialog open={!!activatePromo} onClose={() => setActivatePromo(null)} maxWidth="xs" fullWidth>
          <DialogTitle sx={{ fontWeight: 800 }}>Activate this promotion?</DialogTitle>
          <DialogContent>
            <Typography>
              {activatePromo ? titleForPromo(activatePromo, apiCatalogMap) : ''} — {activatePromo?.min_percentage}% off from{' '}
              {activatePromo?.start_date} to {activatePromo?.end_date}.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setActivatePromo(null)} sx={{ textTransform: 'none' }}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handleActivate}
              disabled={statusBusy}
              startIcon={<CheckCircleIcon />}
              sx={{ textTransform: 'none', bgcolor: '#16a34a', '&:hover': { bgcolor: '#15803d' } }}
            >
              {statusBusy ? 'Activating…' : 'Activate'}
            </Button>
          </DialogActions>
        </Dialog>

        <DeactivateModal
          open={!!deactivatePromo}
          promo={deactivatePromo}
          loading={statusBusy}
          onClose={() => setDeactivatePromo(null)}
          onConfirm={handleDeactivate}
        />

        <ConfigureModal
          open={configureOpen}
          onClose={closeConfigure}
          campaign={selectedCampaign || (editingId ? { name: titleForPromo({ campaign_key: campaignKey, campaign_name: selectedCampaign?.name, discount_type: apiType }, apiCatalogMap), gradient: 'linear-gradient(135deg,#FF7700,#fdba74)' } : null)}
          editingId={editingId}
          percent={percent}
          setPercent={setPercent}
          startDate={startDate}
          setStartDate={setStartDate}
          endDate={endDate}
          setEndDate={setEndDate}
          monthDate={monthDate}
          setMonthDate={setMonthDate}
          formErrors={formErrors}
          overlapWarning={overlapWarning}
          saving={saving}
          onSave={handleSave}
          onReset={() => {
            const camp = resolveCampaign(campaignKey, apiCatalogMap);
            if (camp) {
              setPercent(String(camp.percent));
              const range = suggestedRangeFor(camp.key);
              setStartDate(range.start);
              setEndDate(range.end);
              setMonthDate(startOfMonth(range.start));
            }
            setFormErrors({});
          }}
          coveredRanges={coveredRanges}
          onClearDateErrors={() => setFormErrors((prev) => ({ ...prev, startDate: null, endDate: null }))}
        />
      </Box>
    </LocalizationProvider>
  );
};

export default Promotions;
